import { lazy, type RefObject, type ComponentType, type LazyExoticComponent } from 'react';
import type { ContactShell } from './contact-types';
import type { ContactPlatform } from './contact-drawer-url-state';

/**
 * The detail views the right panel can render. Order is the canonical rail
 * order. Kept as a const tuple so the nuqs `parseAsStringLiteral` parser and the
 * `ContactView` type stay in lockstep. Mentions and recent media live as tabs
 * under the profile view, not as standalone rail entries.
 */
export const CONTACT_VIEWS = [
  'profile',
  'affiliation',
  // Contractual collabs (sponsored posts): list + per-collab drill-in.
  'collabs',
  'ads',
  'gifting',
  // Not a rail entry: reached only from the identity header's primary CTA
  // (Invite) and rendered as a full-height email composer overlay in the detail
  // area. Kept in the tuple so the nuqs URL literal and `ContactView` accept it.
  'outreach',
] as const;

export type ContactView = (typeof CONTACT_VIEWS)[number];

/**
 * Props every view receives. Views are pure: they read their slice of the
 * shell and the active platform.
 */
export interface ContactViewProps {
  headerActionsRef?: RefObject<HTMLDivElement | null>;
  contact: ContactShell;
  platform: ContactPlatform;
  /** Closes the full contact drawer. */
  onClose?: () => void;
  /** Pops one level within the active contact view. */
  onBack?: () => void;
}

/**
 * Lazy registry: `ContactView` -> the code-split view component. A single
 * <Suspense> in ContactDetailPanel wraps whichever one is active, so only the
 * selected view's bundle loads. The Rail+Views phase fills these modules in.
 */
export const CONTACT_VIEW_REGISTRY: Record<
  ContactView,
  LazyExoticComponent<ComponentType<ContactViewProps>>
> = {
  profile: lazy(() => import('./views/ContactProfileView')),
  affiliation: lazy(() => import('./views/ContactAffiliationView')),
  collabs: lazy(() => import('./views/ContactCollabsView')),
  ads: lazy(() => import('./views/ContactAdsView')),
  gifting: lazy(() => import('./views/ContactGiftingView')),
  outreach: lazy(() => import('./views/ContactOutreachView')),
};
